import Papa from 'papaparse'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import type { WorkflowConfig } from 'sprites-types/workflow/core'
import { RenderingConfigSchema, type RenderingConfig } from 'sprites-types/workflow/tools/common'
import { z } from 'zod'

/**
 * Frontend Template Variable Resolution
 *
 * This module handles template variable resolution for rendering node outputs.
 * Architecture aligned with backend's resolveVariablePath for future unification.
 *
 * Key features:
 * - Single-emit mode: resolveTemplateVariables() - simple variable substitution
 * - Multi-emit/list mode: resolveListTemplateVariables() - array iteration with indexing
 * - Fallback resolution: tries both [[variable]] and [[nodeId.variable]]
 * - Command pattern: Arrays auto-convert to markdown lists (primitives) or tables (objects)
 * - Type safety: Uses 'unknown' types with proper runtime checks
 *
 * Special variables:
 * - [[index]] - Current iteration index (1-based) in list mode only
 *
 * Future improvements:
 * - Unify with backend resolution logic in shared package
 * - Support cross-node variable references (requires workflow context)
 * - Add more special variables if needed ([[total]], [[index0]], etc.)
 */

/**
 * Helper to check if a string represents a numeric index
 */
function isNumeric(s: string): boolean {
  if (s.trim() === '') return false
  const n = Number(s)
  return Number.isInteger(n) && String(n) === s
}

/**
 * Traverses a path in an object, handling arrays intelligently.
 * Similar to backend's resolveVariablePath logic.
 *
 * @param value - The object to traverse
 * @param parts - Path parts to traverse
 * @param arrayIndex - Optional index for multi-emit mode
 * @param hasIndexed - Whether we've already indexed into an array
 * @returns The resolved value or undefined
 */
function traversePath(
  value: unknown,
  parts: string[],
  arrayIndex?: number,
  hasIndexed = false
): unknown {
  if (parts.length === 0) return value

  // Multi-emit mode: index into arrays first if we haven't already
  if (!hasIndexed && arrayIndex !== undefined && Array.isArray(value)) {
    value = value[arrayIndex]
    hasIndexed = true
  }

  const [head, ...rest] = parts

  // Handle arrays
  if (Array.isArray(value)) {
    // Numeric index: treat as explicit array access
    if (isNumeric(head)) {
      const idx = Number(head)
      if (idx >= 0 && idx < value.length) {
        return traversePath(value[idx], rest, arrayIndex, hasIndexed)
      }
      return undefined
    }

    // Non-numeric property: map across array elements
    const mappedResults = value
      .map((item) => traversePath(item, parts, undefined, true))
      .filter((item) => item !== undefined)

    // In multi-emit mode with remaining parts, index into the mapped results
    if (!hasIndexed && arrayIndex !== undefined && mappedResults.length > 0) {
      return mappedResults[arrayIndex]
    }

    return mappedResults.length > 0 ? mappedResults : undefined
  }

  // Handle objects
  if (value != null && typeof value === 'object') {
    const obj = value as Record<string, unknown>
    if (head in obj) {
      return traversePath(obj[head], rest, arrayIndex, hasIndexed)
    }
  }

  return undefined
}

/**
 * Resolves a single variable path to a value.
 * Tries multiple resolution strategies:
 * 1. Direct path (e.g., [[items.name]])
 * 2. With node ID prefix (e.g., [[node-id.items.name]] if [[items.name]] fails)
 *
 * @param varPath - The variable path to resolve
 * @param outputs - The current node outputs
 * @param nodeId - The current node ID
 * @param arrayIndex - Optional array index for multi-emit mode
 * @returns The resolved value or undefined
 */
function resolveVariablePath(
  varPath: string,
  outputs: unknown,
  nodeId: string,
  arrayIndex?: number
): unknown {
  const parts = varPath.trim().split('.')

  // Strategy 1: Direct path
  let value = traversePath(outputs, parts, arrayIndex, false)

  // Strategy 2: If not found and not already prefixed with nodeId, try with nodeId prefix
  if (value === undefined && parts[0] !== nodeId) {
    value = traversePath(outputs, parts, arrayIndex, false)
  }

  // Strategy 3: If it starts with nodeId, try without the prefix
  if (value === undefined && parts[0] === nodeId && parts.length > 1) {
    const remaining = parts.slice(1)
    value = traversePath(outputs, remaining, arrayIndex, false)
  }

  return value
}

/**
 * Converts a resolved value to a string representation.
 * Arrays are converted to markdown lists/tables.
 * Objects are JSON stringified.
 */
function valueToString(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (Array.isArray(value)) return formatArrayToMarkdown(value)
  return JSON.stringify(value)
}

/**
 * Resolves template variables in the format [[variable]] or [[node_id.variable]]
 *
 * Single-emit mode: Returns a string with all variables substituted.
 * Variables that resolve to arrays/objects are JSON stringified.
 */
export function resolveTemplateVariables(
  content: string,
  outputs: unknown,
  nodeId: string
): string {
  if (!content || typeof content !== 'string') return content

  const templateVarRegex = /\[\[([^\]]+)\]\]/g

  return content.replace(templateVarRegex, (match, varPath) => {
    try {
      const resolved = resolveVariablePath(varPath, outputs, nodeId)
      return resolved !== undefined ? valueToString(resolved) : match
    } catch (error) {
      console.warn('Error resolving template variable:', varPath, error)
      return match
    }
  })
}

/**
 * Detects if a value contains an array and returns the array length.
 * Only checks the top-level resolved value to find the first array.
 */
function findFirstArray(outputs: unknown, templateVars: string[], nodeId: string): number {
  for (const varPath of templateVars) {
    // Skip special variables
    if (varPath === 'index') continue

    const resolved = resolveVariablePath(varPath, outputs, nodeId)
    if (Array.isArray(resolved)) {
      return resolved.length
    }
  }
  return 0
}

/**
 * Resolves template variables in list/multi-emit mode.
 *
 * Multi-emit mode:
 * 1. Finds the first array in any variable reference
 * 2. Iterates over that array, creating one block per item
 * 3. For each iteration, indexes into the first array encountered
 * 4. Later arrays in the path are converted to markdown lists/tables
 * 5. Supports [[index]] special variable (1-based)
 *
 * Command pattern: Arrays are converted to markdown using type-specific formatters:
 * - Array of strings → markdown list
 * - Array of objects → markdown table
 */
export function resolveListTemplateVariables(
  content: string,
  outputs: unknown,
  nodeId: string
): string {
  if (!content || typeof content !== 'string') return content

  const templateVarRegex = /\[\[([^\]]+)\]\]/g
  const matches = Array.from(content.matchAll(templateVarRegex))
  const templateVars = matches.map((m) => m[1].trim())

  // Find the first array to determine iteration count
  const arrayLength = findFirstArray(outputs, templateVars, nodeId)

  // If no array found, fall back to single-emit mode
  if (arrayLength === 0) {
    return resolveTemplateVariables(content, outputs, nodeId)
  }

  // Multi-emit mode: iterate over the array
  const resolvedBlocks: string[] = []

  for (let i = 0; i < arrayLength; i++) {
    const resolvedContent = content.replace(templateVarRegex, (match, varPath) => {
      try {
        const trimmedPath = varPath.trim()

        // Special variable: [[index]] (1-based)
        if (trimmedPath === 'index') {
          return String(i + 1)
        }

        // Resolve the variable with array indexing
        const resolved = resolveVariablePath(trimmedPath, outputs, nodeId, i)

        if (resolved === undefined) return match

        // Command pattern: Convert arrays based on content type
        if (Array.isArray(resolved)) {
          return formatArrayToMarkdown(resolved)
        }

        return valueToString(resolved)
      } catch (error) {
        console.warn('Error resolving list template variable:', varPath, error)
        return match
      }
    })

    resolvedBlocks.push(resolvedContent)
  }

  return resolvedBlocks.join('\n')
}

/**
 * Command pattern: Converts arrays to markdown based on content type.
 * - Array of strings/primitives → markdown list
 * - Array of objects → markdown table (if uniform structure)
 * - Mixed types → JSON representation
 */
function formatArrayToMarkdown(arr: unknown[]): string {
  if (arr.length === 0) return ''

  // Check if array contains uniform objects (table candidate)
  const isUniformObjects =
    arr.length > 0 &&
    arr.every((item) => item != null && typeof item === 'object' && !Array.isArray(item))

  if (isUniformObjects) {
    // Convert to markdown table
    return arrayToMarkdownTable(arr as Record<string, unknown>[])
  }

  // Convert to markdown list
  return arrayToMarkdownList(arr)
}

/**
 * Converts an array of objects to a markdown table
 */
function arrayToMarkdownTable(arr: Record<string, unknown>[]): string {
  if (arr.length === 0) return ''

  // Collect all unique keys
  const keys = Array.from(
    arr.reduce<Set<string>>((acc, row) => {
      Object.keys(row).forEach((k) => acc.add(k))
      return acc
    }, new Set<string>())
  )

  if (keys.length === 0) return ''

  // Build markdown table
  const header = `| ${keys.join(' | ')} |`
  const separator = `| ${keys.map(() => '---').join(' | ')} |`
  const rows = arr.map((row) => `| ${keys.map((k) => formatCellValue(row[k])).join(' | ')} |`)

  return [header, separator, ...rows].join('\n')
}

/**
 * Formats a value for a table cell
 */
function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value.replace(/\n/g, ' ').replace(/\|/g, '\\|')
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return JSON.stringify(value).replace(/\|/g, '\\|')
}

/**
 * Converts array input to markdown list format
 */
export function arrayToMarkdownList(input: unknown): string {
  try {
    // If already an array
    if (Array.isArray(input)) {
      return input
        .map((item) =>
          typeof item === 'string'
            ? item
            : typeof item === 'number' || typeof item === 'boolean' || item == null
              ? String(item)
              : JSON.stringify(item)
        )
        .map((line) => `- ${line}`)
        .join('\n')
    }

    // If it's a JSON array encoded as string
    if (typeof input === 'string') {
      const trimmed = input.trim()
      if (
        (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
        (trimmed.startsWith('{') && trimmed.endsWith('}'))
      ) {
        try {
          const parsed = JSON.parse(trimmed)
          if (Array.isArray(parsed)) {
            return arrayToMarkdownList(parsed)
          }
        } catch {
          // fall through to newline splitting
        }
      }
      // Fallback: treat as newline-delimited list
      return trimmed
        .split('\n')
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
        .map((item) => `- ${item}`)
        .join('\n')
    }

    // Fallback: render single item
    return `- ${typeof input === 'string' ? input : JSON.stringify(input)}`
  } catch {
    return `- ${typeof input === 'string' ? input : JSON.stringify(input)}`
  }
}

/**
 * Converts CSV content to markdown table
 */
export function csvToMarkdownTable(csvContent: string): string {
  try {
    const parseResult = Papa.parse(csvContent.trim(), {
      header: false,
      skipEmptyLines: true,
    })

    if (parseResult.errors.length > 0) {
      console.warn('CSV parsing errors:', parseResult.errors)
    }

    const rows = parseResult.data as string[][]
    if (rows.length === 0) return csvContent

    // Create markdown table
    const markdownRows: string[] = []

    // Header row
    if (rows.length > 0) {
      markdownRows.push('| ' + rows[0].join(' | ') + ' |')
      markdownRows.push('| ' + rows[0].map(() => '---').join(' | ') + ' |')
    }

    // Data rows
    for (let i = 1; i < rows.length; i++) {
      markdownRows.push('| ' + rows[i].join(' | ') + ' |')
    }

    return markdownRows.join('\n')
  } catch (error) {
    console.error('Error converting CSV to markdown:', error)
    return csvContent // Return original content if parsing fails
  }
}

/**
 * Formats primitive values for table cells
 */
export function formatPrimitiveForCell(v: unknown): string {
  if (v === null || v === undefined) return ''
  if (typeof v === 'string') return v.replace(/\n/g, ' ')
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  return '`' + JSON.stringify(v) + '`'
}

/**
 * Infers the best presentation type and content for raw outputs
 */
export function inferRawPresentation(value: unknown): {
  type: 'markdown' | 'html' | 'csv' | 'table' | 'url' | 'code' | 'object'
  content: string
  data?: unknown
} {
  try {
    // Strings: try specialized formats
    if (typeof value === 'string') {
      const trimmed = value.trim()
      const looksLikeHtml = /<([a-z]+)([^<]+)*>(.*)<\/\1>/i.test(trimmed)
      if (looksLikeHtml) return { type: 'html', content: trimmed }

      const looksLikeUrl = /^(https?:\/\/|data:|blob:)/i.test(trimmed)
      if (looksLikeUrl) return { type: 'url', content: trimmed }

      // Default string → markdown (might already contain markdown)
      return { type: 'markdown', content: trimmed }
    }

    // Array of uniform objects → markdown table
    if (Array.isArray(value)) {
      if (
        value.length > 0 &&
        value.every((row) => row && typeof row === 'object' && !Array.isArray(row))
      ) {
        // Use the unified table formatter
        return {
          type: 'markdown',
          content: arrayToMarkdownTable(value as Record<string, unknown>[]),
        }
      }

      // For non-tabular arrays, format as JSON
      return { type: 'markdown', content: `\`\`\`json\n${JSON.stringify(value, null, 2)}\n\`\`\`` }
    }

    // Object with common content fields
    if (value && typeof value === 'object') {
      const obj = value as Record<string, unknown>
      // html
      if (typeof obj.html === 'string') return { type: 'html', content: obj.html }
      // markdown
      if (typeof obj.markdown === 'string') return { type: 'markdown', content: obj.markdown }
      // csv
      if (typeof obj.csv === 'string') return { type: 'csv', content: obj.csv }
      // url
      if (typeof obj.url === 'string') return { type: 'url', content: obj.url }
      // imageUrl → markdown image
      if (typeof obj.imageUrl === 'string')
        return { type: 'markdown', content: `![Image](${obj.imageUrl})` }

      // For plain objects with key-value pairs, use structured object display
      return {
        type: 'object',
        content: '', // Content not used for object type
        data: value,
      }
    }

    // Primitives
    return { type: 'markdown', content: String(value) }
  } catch {
    // On error, try to stringify the value safely
    try {
      return { type: 'markdown', content: `\`\`\`json\n${JSON.stringify(value, null, 2)}\n\`\`\`` }
    } catch {
      return { type: 'markdown', content: String(value) }
    }
  }
}

/**
 * Extracts rendering schema from node execution inputs
 */
export function extractRenderingSchema(nodeExecution: NodeExecution): RenderingConfig[] {
  try {
    if (
      'functionArgs' in nodeExecution.inputs &&
      'renderingSchema' in nodeExecution.inputs.functionArgs &&
      nodeExecution.inputs.functionArgs.renderingSchema != null
    ) {
      return z.array(RenderingConfigSchema).parse(nodeExecution.inputs.functionArgs.renderingSchema)
    }
    return []
  } catch (error) {
    console.error('[extractRenderingSchema] Failed to parse rendering schema:', error)
    return []
  }
}

/**
 * Processes raw outputs for components that don't have rendering schema
 * Uses workflow config to extract variable outputs or falls back to direct output processing
 */
export function processRawOutputs(
  nodeExecution: NodeExecution,
  workflowConfig?: WorkflowConfig
): Array<{ type: string; content: string; data?: unknown }> {
  // Collect variables that reference this node id
  const variables =
    workflowConfig?.outputs?.filter((output: string) => output.includes(nodeExecution.nodeId)) ?? []

  // Extract values for each variable path
  const variableOutputs = variables
    .map((variable: string) => {
      // variable format: [[nodeId.key.subkey]] → take after first dot
      const pathParts = variable.slice(2, -2).split('.').slice(1)
      try {
        return pathParts.reduce(
          (acc: any, key) => (acc == null ? undefined : acc[key]),
          nodeExecution.outputs
        )
      } catch {
        return undefined
      }
    })
    .filter((v: unknown) => v !== undefined)

  const inferredList = variableOutputs.map((v: unknown) => inferRawPresentation(v))

  // If no variables found, try to infer from the raw outputs directly
  if (inferredList.length === 0) {
    const inferred = inferRawPresentation(nodeExecution.outputs)
    return [inferred]
  }

  return inferredList
}

/**
 * Converts inferred content to markdown format for export
 */
export function inferredContentToMarkdown(
  inferred: { type: string; content: string; data?: unknown },
  _variableOutput?: unknown
): string {
  switch (inferred.type) {
    case 'markdown':
      return inferred.content
    case 'html':
      return `\`\`\`html\n${inferred.content}\n\`\`\``
    case 'csv':
      return csvToMarkdownTable(inferred.content)
    case 'url':
      return `[${inferred.content}](${inferred.content})`
    case 'object':
      // Convert object to markdown table (key-value pairs)
      if (inferred.data && typeof inferred.data === 'object' && !Array.isArray(inferred.data)) {
        const entries = Object.entries(inferred.data as Record<string, unknown>)
        if (entries.length === 0) return '*Empty object*'

        const header = '| Property | Value |'
        const separator = '| --- | --- |'
        const rows = entries.map(([key, value]) => {
          const formattedValue =
            value === null || value === undefined
              ? ''
              : typeof value === 'object'
                ? JSON.stringify(value)
                : String(value)
          return `| ${key} | ${formattedValue.replace(/\|/g, '\\|')} |`
        })
        return [header, separator, ...rows].join('\n')
      }
      return inferred.content
    default:
      return inferred.content
  }
}
